import type { FirebaseApp } from 'firebase/app';
import type {
  ActionCodeSettings,
  AuthCredential,
  AuthProvider,
  IdTokenResult,
  PopupRedirectResolver,
  User,
  UserCredential,
} from 'firebase/auth';

import type { AuthBackendFactory } from './firebase-platform.js';
import type { PhoneVerificationChallenge } from '#lib/phone-verification-port.js';

import * as fb from 'firebase/auth';
import { partial } from 'lodash-es';

import { createContainer } from '@elara/core/di';

/** Emulator connection options, mirroring `connectAuthEmulator`'s third argument. */
export interface AuthEmulatorOptions {
  disableWarnings?: boolean;
}

/**
 * Builds the auth container around a platform-injected backend factory rather than calling
 * `getAuth()` directly, so the persistence layer (browser vs React Native) is decided at the
 * composition root. See ELARA-689 and {@link AuthBackendFactory}.
 *
 * This is the **JS `firebase` SDK** implementation of the contract. React Native composes a native
 * (`@react-native-firebase/auth`) container against the same member names instead (ELARA-982), so
 * shared consumers must only ever depend on the members below — never on {@link backend}, whose
 * value is the SDK-specific `Auth` instance and differs per platform.
 */
export function createAuthContainer(firebase: FirebaseApp, createAuthBackend: AuthBackendFactory) {
  const $auth = createContainer('auth')
    // SDK-SPECIFIC. Shared code must not read this — it is the JS `Auth` here and a native RNFB
    // `Auth` on React Native. Use `getCurrentUser` / `connectEmulator` instead.
    .providedLazy('backend', () => createAuthBackend(firebase))
    // The signed-in user, read imperatively (outside the `onAuthStateChanged` cascade) by the flow
    // transport's unauthenticated-response handler. A container member rather than `backend.currentUser`
    // so shared code stays off the SDK-specific instance.
    .dependent('getCurrentUser', ['backend'], ({ backend }) => {
      return (): User | undefined => backend.currentUser ?? undefined;
    })
    // Emulator wiring lives on the container for the same reason: `firebase/auth`'s
    // `connectAuthEmulator` only accepts a JS `Auth`, so `FirebaseContainer.startEmulators` delegates
    // here and each platform connects its own SDK's emulator (ELARA-982).
    .dependent('connectEmulator', ['backend'], ({ backend }) => {
      return (url: string, options?: AuthEmulatorOptions): void =>
        fb.connectAuthEmulator(backend, url, { disableWarnings: options?.disableWarnings ?? false });
    })
    .provided('GoogleAuthProvider', fb.GoogleAuthProvider)
    .provided('EmailAuthProvider', fb.EmailAuthProvider)
    // `OAuthProvider` builds federated credentials from a provider id + raw token (e.g.
    // `new OAuthProvider('apple.com').credential({ idToken, rawNonce })`). Used by the native social
    // sign-in port to exchange an Apple identity token for a Firebase credential (ELARA-728).
    .provided('OAuthProvider', fb.OAuthProvider)
    .dependent('createUserWithEmailAndPassword', ['backend'], ({ backend }) => {
      return partial(fb.createUserWithEmailAndPassword, backend);
    })
    .dependent('signInWithEmailAndPassword', ['backend'], ({ backend }) => {
      return partial(fb.signInWithEmailAndPassword, backend);
    })
    // `signInWithPopup` is web-only (no popups on React Native) — it is `undefined` in Firebase's RN
    // build, so `partial(fb.signInWithPopup, ...)` would throw "Expected a function" the moment the
    // eager DI `.use` resolves this provider. Reference it lazily inside the returned function so
    // resolution stays RN-safe; only an actual call (which mobile never makes) would surface it.
    .dependent('signInWithPopup', ['backend'], ({ backend }) => {
      return (provider: AuthProvider, resolver?: PopupRedirectResolver): Promise<UserCredential> =>
        fb.signInWithPopup(backend, provider, resolver);
    })
    // Signs in with a pre-built federated credential. The native social sign-in port (ELARA-728)
    // performs the OAuth dance itself (native Google / Apple UI), then hands the resulting Firebase
    // credential here — the React Native analogue of the web's `signInWithPopup`.
    .dependent('signInWithCredential', ['backend'], ({ backend }) => {
      return (credential: AuthCredential): Promise<UserCredential> => fb.signInWithCredential(backend, credential);
    })
    .dependent('signOut', ['backend'], ({ backend }) => partial(fb.signOut, backend))
    .dependent('onAuthStateChanged', ['backend'], ({ backend }) => partial(fb.onAuthStateChanged, backend))
    .dependent('onIdTokenChanged', ['backend'], ({ backend }) => partial(fb.onIdTokenChanged, backend))
    .dependent('sendPasswordResetEmail', ['backend'], ({ backend }) => {
      return async (email: string, actionCodeSettings?: ActionCodeSettings) =>
        fb.sendPasswordResetEmail(backend, email, actionCodeSettings);
    })
    .dependent('confirmPasswordReset', ['backend'], ({ backend }) => {
      return async (oobCode: string, newPassword: string) => fb.confirmPasswordReset(backend, oobCode, newPassword);
    })
    .dependent('applyActionCode', ['backend'], ({ backend }) => {
      return async (oobCode: string) => fb.applyActionCode(backend, oobCode);
    })
    .dependent('verifyPasswordResetCode', ['backend'], ({ backend }) => {
      return async (oobCode: string) => fb.verifyPasswordResetCode(backend, oobCode);
    })
    .dependent('getIdTokenResult', ['backend'], ({ backend }) => {
      return async (forceRefresh?: boolean): Promise<IdTokenResult | undefined> => {
        const user = backend.currentUser;
        if (!user) return undefined;
        return user.getIdTokenResult(forceRefresh);
      };
    })
    .dependent('updatePassword', ['backend'], ({ backend }) => {
      return async (newPassword: string): Promise<void> => {
        const user = backend.currentUser;
        if (!user) throw new Error('No authenticated user');
        return fb.updatePassword(user, newPassword);
      };
    })
    .dependent('reauthenticateWithCredential', ['backend'], ({ backend }) => {
      return async (credential: fb.AuthCredential) => {
        const user = backend.currentUser;
        if (!user) throw new Error('No authenticated user');
        return fb.reauthenticateWithCredential(user, credential);
      };
    })
    .dependent('updateProfile', ['backend'], ({ backend }) => {
      return async (profile: { displayName?: string | null; photoURL?: string | null }): Promise<void> => {
        const user = backend.currentUser;
        if (!user) throw new Error('No authenticated user');
        return fb.updateProfile(user, profile);
      };
    })
    // --- Phone verification (Firebase Phone Auth) ---
    // Web-oriented: `RecaptchaVerifier` is the browser app-verifier. A future RN
    // path would inject a native verifier through the same `linkPhoneNumber` seam.
    .dependent('createRecaptchaVerifier', ['backend'], ({ backend }) => {
      return (container: string | HTMLElement): fb.RecaptchaVerifier =>
        new fb.RecaptchaVerifier(backend, container, { size: 'invisible' });
    })
    .dependent('linkPhoneNumber', ['backend'], ({ backend }) => {
      // Puts a verified phone on the CURRENT user (does not create/switch accounts). Returns a
      // challenge; call `.confirm(code)` to finish.
      //
      // Sends the OTP with `PhoneAuthProvider.verifyPhoneNumber` and finishes with `updatePhoneNumber`,
      // which adds a number or replaces the current one. Not `linkWithPhoneNumber`: it first asserts the
      // user has no phone provider, against `user.providerData`, which the SDK's reload MERGES with the
      // server's list rather than replacing. A number removed outside this browser (mobile, the
      // console) therefore stays in that list, and every later link fails with
      // `auth/provider-already-linked` until the user signs in again. It also rejected "Change number",
      // where the user really does have a phone linked.
      //
      // `appVerifier` is optional because it is a web-only concept: React Native's native phone auth
      // verifies the app via APNs silent push / Play Integrity and has no verifier to pass
      // (ELARA-982). The JS SDK genuinely requires one, so its absence is rejected here.
      return async (phoneNumber: string, appVerifier?: fb.ApplicationVerifier): Promise<PhoneVerificationChallenge> => {
        const user = backend.currentUser;
        if (!user) throw new Error('No authenticated user');
        if (!appVerifier) throw new Error('An app verifier is required to link a phone number on the web');
        const verificationId = await new fb.PhoneAuthProvider(backend).verifyPhoneNumber(phoneNumber, appVerifier);
        return {
          async confirm(code: string) {
            await fb.updatePhoneNumber(user, fb.PhoneAuthProvider.credential(verificationId, code));
          },
        };
      };
    });
  return $auth;
}

export type $AuthContainer = ReturnType<typeof createAuthContainer>;
export type AuthContainer = $AuthContainer['use'];
